import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import {
  checkboxesFeature,
  hotkeysCoreFeature,
  syncDataLoaderFeature,
} from "@headless-tree/core";
import { useTree } from "@headless-tree/react";
import styles from "./json-field-tree.module.scss";
import {
  JsonFieldItem,
  JsonFieldTree as JsonFieldTreeData,
} from "./build-json-field-tree";

const cssClassName = "json-field-tree";

/** The library insists on a root item. It never renders: its children are the
 *  top-level fields of the event. */
const ROOT_ID = "__root__";

const ROOT_ITEM: JsonFieldItem = {
  id: ROOT_ID,
  label: "",
  parentId: null,
  level: -1,
  isFolder: true,
  isSelectable: false,
  childIds: [],
};

/** Nesting indent per level, matching the structure trees (`tree-list`). */
const INDENT_STEP = 28;

/** When a folder name matches a search, return the folder plus every row
 *  beneath it (sub-folders and leaves). Ids referenced in `childIds` that
 *  don't exist in `tree.items` are skipped — otherwise the row list would
 *  show a placeholder for a row that has no real data. */
const computeDescendantIds = (
  tree: JsonFieldTreeData,
  folderId: string,
): Set<string> => {
  const out = new Set<string>();
  const queue: string[] = [folderId];
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i];
    if (out.has(id) || !tree.items[id]) continue;
    out.add(id);
    for (const childId of tree.items[id].childIds) {
      queue.push(childId);
    }
  }
  return out;
};

/** When the user types in the search box:
 *  - A leaf whose label matches is added to the result list.
 *  - A folder whose label matches is added together with every row inside
 *    it — so selecting the folder name picks the same rows as selecting any
 *    field beneath it. */
const computeMatchingIds = (
  tree: JsonFieldTreeData,
  search: string | null,
): Set<string> => {
  if (!search) return new Set();
  const needle = search.toLowerCase();
  const matches = new Set<string>();
  for (const id of Object.keys(tree.items)) {
    const item = tree.items[id];
    if (item.id === ROOT_ID) continue;
    if (!item.label.toLowerCase().includes(needle)) continue;
    if (item.isFolder) {
      computeDescendantIds(tree, id).forEach((descId) => matches.add(descId));
    } else {
      matches.add(id);
    }
  }
  return matches;
};

/** The spine = matching leaves ∪ the folders on the way up to the root.
 *  That is what survives in the rendered tree while a search is active:
 *  everything outside the spine gets pruned by `getChildren`. */
const computeAncestorIds = (
  tree: JsonFieldTreeData,
  matchingIds: Set<string>,
): Set<string> => {
  if (matchingIds.size === 0) return new Set();
  const spine = new Set(matchingIds);
  matchingIds.forEach((id) => {
    let cur: string | null | undefined = tree.items[id]?.parentId;
    while (cur) {
      spine.add(cur);
      cur = tree.items[cur]?.parentId;
    }
  });
  return spine;
};

export interface JsonFieldTreeProps {
  tree: JsonFieldTreeData;
  checkedIds: string[];
  onCheckedIdsChange: (ids: string[]) => void;
  ariaLabel?: string;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  noMatchesText?: string;
}

export const JsonFieldTree = ({
  tree,
  checkedIds,
  onCheckedIdsChange,
  ariaLabel,
  searchPlaceholder = "Search",
  searchAriaLabel = "Search fields",
  noMatchesText = "No matches",
}: JsonFieldTreeProps) => {
  // Expansion is view state only, so the component keeps it. Everything the
  // widget stores (selection, names) stays with the editor.
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Local search-string state drives both the input and the data-layer
  // filter. Owning the spine computation ourselves is cheaper and clearer
  // than the library's searchFeature (which dims in place rather than
  // pruning) and keeps `treeInstance.getItems()` self-consistent.
  const [search, setSearch] = useState<string | null>(null);
  const searchActive = search != null && search !== "";

  const matchingIds = useMemo(
    () => computeMatchingIds(tree, search),
    [tree, search],
  );
  const spineIds = useMemo(
    () => computeAncestorIds(tree, matchingIds),
    [tree, matchingIds],
  );

  // When the user types in the search box, only the row list is filtered —
  // folders are never opened or closed by the query. Whatever the user
  // expanded or collapsed before, during, or after the query stays exactly
  // as they left it.

  const treeInstance = useTree<JsonFieldItem>({
    rootItemId: ROOT_ID,
    dataLoader: {
      getItem: (id) =>
        id === ROOT_ID
          ? ROOT_ITEM
          : tree.items[id] ?? { ...ROOT_ITEM, id, label: id },
      getChildren: (id) => {
        const baseChildren =
          id === ROOT_ID ? tree.rootIds : tree.items[id]?.childIds ?? [];
        if (!searchActive) return baseChildren;
        return baseChildren.filter((childId) => spineIds.has(childId));
      },
    },
    getItemName: (item) => item.getItemData().label,
    isItemFolder: (item) => item.getItemData().isFolder,
    state: { checkedItems: checkedIds, expandedItems },
    setCheckedItems: onCheckedIdsChange,
    setExpandedItems,

    canCheckFolders: false,
    propagateCheckedState: false,
    ignoreHotkeysOnInputs: true,
    features: [syncDataLoaderFeature, hotkeysCoreFeature, checkboxesFeature],
  });

  // Parents start expanded: the user opens the payload to find a field rather
  // than expanding six levels by hand. A new payload also clears the search —
  // the user inputs a fresh event, so the previous query is stale.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setExpandedItems(
      Object.values(tree.items)
        .filter((item) => item.isFolder)
        .map((item) => item.id),
    );
    setSearch(null);
  }, [tree]);

  // The library only rebuilds its visible items when `expandedItems` changes
  // (or on mount). Our data-layer filter at `getChildren` does not by itself
  // force a rebuild, so we schedule one explicitly whenever the search string
  // changes — that's the moment when the visible set may have shrunk.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    treeInstance.rebuildTree();
  }, [search]);

  const items = treeInstance.getItems();
  const noMatches = searchActive && matchingIds.size === 0;

  return (
    <>
      {/* Visible filter input above the list. The spine is computed from
          this string each render — typing prunes non-matching branches out
          of the data the library sees. */}
      <div className={styles[`${cssClassName}__search`]}>
        <input
          type="search"
          className={styles[`${cssClassName}__search-input`]}
          placeholder={searchPlaceholder}
          value={search ?? ""}
          onChange={(e) => setSearch(e.target.value || null)}
          aria-label={searchAriaLabel}
        />
      </div>

      <ul
        className={styles[`${cssClassName}__list`]}
        {...treeInstance.getContainerProps(ariaLabel)}
      >
        {noMatches && (
          <li
            className={cn(
              styles[`${cssClassName}__node`],
              styles[`${cssClassName}__node--empty`],
            )}
          >
            {noMatchesText}
          </li>
        )}
        {items.map((item) => {
          const data = item.getItemData();

          return (
            <li
              key={item.getId()}
              className={styles[`${cssClassName}__node`]}
              style={{ paddingLeft: item.getItemMeta().level * INDENT_STEP }}
              {...item.getProps()}
            >
              <span
                className={cn(styles[`${cssClassName}__row`], {
                  [styles[`${cssClassName}__row--disabled`]]:
                    !data.isFolder && !data.isSelectable,
                })}
              >
                {/* The leading column holds the checkbox for a leaf and the folder
                    icon for a parent: folders cannot be checked, so an empty
                    checkbox box there would be a hole in the row. */}
                <span className={styles[`${cssClassName}__leading`]}>
                  {data.isFolder ? (
                    <span
                      aria-hidden="true"
                      className={styles[`${cssClassName}__folder-icon`]}
                    />
                  ) : (
                    <input
                      type="checkbox"
                      className={styles[`${cssClassName}__checkbox`]}
                      {...(data.isSelectable
                        ? item.getCheckboxProps()
                        : // Disabled inputs get no props from the library, so the
                          // name has to be given here.
                          { disabled: true, "aria-label": data.label })}
                    />
                  )}
                </span>
                <span className={styles[`${cssClassName}__name`]}>
                  {data.label}
                </span>
                {data.isFolder && (
                  <span
                    aria-hidden="true"
                    className={cn(styles[`${cssClassName}__caret`], {
                      [styles[`${cssClassName}__caret--open`]]:
                        item.isExpanded(),
                    })}
                  />
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
};
